import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/home";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const total = heroSlides.length;

  const goTo = useCallback(
    (index: number) => setActive((index + total) % total),
    [total],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [total]);

  return (
    <section className="relative h-[440px] w-full overflow-hidden md:h-[600px]">
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点推荐`}
            title={`${slide.title} - 91蜜桃焦点推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/85 via-secondary-950/55 to-secondary-950/20" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background-50 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col justify-center px-4 md:px-8">
        <div key={heroSlides[active].id} className="max-w-xl animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line" />
            {heroSlides[active].kicker}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-black leading-tight tracking-tight text-background-50 md:text-5xl">
            {heroSlides[active].title}
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-background-100/85 md:text-base">
            {heroSlides[active].subtitle}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {heroSlides[active].tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-background-100/30 px-2.5 py-0.5 text-xs text-background-100"
              >
                {tag}
              </span>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${heroSlides[active].mediaId}`}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-base" />
              查看作品详情
            </Link>
            <Link
              to="/category/movie"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-100/40 bg-secondary-950/30 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-secondary-950/50"
            >
              <i className="ri-compass-3-line text-base" />
              浏览更多影视
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 z-10 flex items-center gap-3 md:right-8">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(active - 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-100/40 bg-secondary-950/30 text-background-50 backdrop-blur transition-colors hover:bg-secondary-950/60"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <div className="flex items-center gap-1.5">
          {heroSlides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张`}
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? "w-6 bg-primary-500" : "w-1.5 bg-background-100/50"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(active + 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-100/40 bg-secondary-950/30 text-background-50 backdrop-blur transition-colors hover:bg-secondary-950/60"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </section>
  );
}